
import { useState } from 'react';
import { AuthGate } from '@client/shared/auth/index.ts';
import { SetupLoginStep } from './SetupLoginStep.tsx';
import { CreateOrgStep } from './CreateOrgStep.tsx';
import { readStashedOwnerName } from './owner-name-storage.ts';

export interface OnboardingWizardProps {
  readonly email: string;
}

type WizardState =
  | { readonly step: 'setup-login' }
  | { readonly step: 'create-org'; readonly ownerName: string };

// A stashed owner name means the /signup screen already set name + password: open on
// create-org. Otherwise (invite link path) start on set-password; a genuinely absent grant
// surfaces SetupLoginStep's honest "sign-up link expired" error rather than silently
// skipping the password step (ticket 04).
function initialWizardState(): WizardState {
  const ownerName = readStashedOwnerName();
  if (ownerName && ownerName.trim().length > 0) return { step: 'create-org', ownerName };
  return { step: 'setup-login' };
}

const STEP_COPY = {
  'setup-login': {
    eyebrow: 'Step 1 of 2',
    heading: 'Create your account',
    blurb: 'Pick a name and a password.',
  },
  'create-org': {
    eyebrow: 'Step 2 of 2',
    heading: 'Setup your group',
    blurb: 'This is the group your teammates will join.',
  },
} as const;

export function OnboardingWizard({ email }: OnboardingWizardProps) {
  const [state, setState] = useState<WizardState>(initialWizardState);
  const copy = STEP_COPY[state.step];

  return (
    <AuthGate eyebrow={copy.eyebrow} heading={copy.heading} intro={copy.blurb}>
      {state.step === 'setup-login' ? (
        <SetupLoginStep
          email={email}
          onComplete={({ name }) => setState({ step: 'create-org', ownerName: name })}
        />
      ) : (
        <CreateOrgStep ownerName={state.ownerName} />
      )}
    </AuthGate>
  );
}
